import NewLayout from "@components/new-ui/Layout";
import React, { useEffect, useState } from "react";
import { supabase } from "@utils/supabase";
import axios from "axios";
import { v4 as uuid } from "uuid";
import qrcode from "@utils/qrcode";

type RealtimeMessage = {
    message: string;
    status: string;
    url?: string;
};

const useBroadcast = (channelName: string) => {
    const [message, setMessage] = useState<RealtimeMessage | null>(null);

    useEffect(() => {
        const channel = supabase.channel(channelName);

        channel.on<RealtimeMessage>("broadcast", { event: "websiteToAIBotFlow" }, (data) => {
            console.log("New message received:", data);
            setMessage(data.payload);
        });

        channel.subscribe();

        return () => {
            channel.unsubscribe();
        };
    }, [channelName]);

    const sendMessage = (message: RealtimeMessage) => {
        supabase.channel(channelName).send({
            type: "broadcast",
            event: "message",
            payload: message,
        });
    };

    const clearMessage = () => {
        setMessage(null);
    };

    return { message, sendMessage, clearMessage };
};

const WhatsappAIChatBot: React.FC = () => {
    const [website, setWebsite] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [socketId, setSocketId] = useState<string>("");
    const { message, clearMessage } = useBroadcast(socketId);
    const [qr, setQr] = useState<string>("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [quality, setQuality] = useState("single_page");

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    const generateQr = async (url: string) => {
        const qrCode = await qrcode(url);
        setQr(qrCode);
    };

    useEffect(() => {
        if (message?.status === "success") {
            generateQr(message?.url as string);
        }
    }, [message]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (website) {
            clearMessage();
            setQr("");
            const id = uuid();
            setSocketId(id);
            await axios.post("/api/v1/website-to-ai-bot", {
                website,
                socketId: id,
                phoneNumber,
                quality,
            });
            alert(`Website submitted: ${website}`);
        } else {
            alert("Please enter a valid website URL!");
        }
    };

    return (
        <NewLayout
            title="Try our AI Chat Bot on WhatsApp: Create chat links now!"
            description="Use a FREE WhatsApp Link Generator to make your WhatsApp Business details more accessible to potential and future customers to drive engagement and revenue."
            canonical="https://gallabox.com/whatsapp-link-generator"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/whatsapp-link-gen/og.png" }}
        >
            <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-500 to-indigo-600 overflow-auto mt-10">
                <div className="bg-white shadow-lg rounded-lg p-8 max-w-lg w-full my-8">
                    <h1 className="text-3xl font-bold text-center text-indigo-600 mb-4">Website to AI Chat Bot</h1>
                    <p className="text-gray-700 text-center mb-6">Enter your website URL to connect with our AI-powered bot.</p>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <input
                            placeholder="Ex: 918608031561"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <input
                            type="url"
                            placeholder="https://example.com"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <div className="w-full pt-1 text-md text-indigo-500 ">
                            <a href="#" onClick={() => toggleModal()}>
                                Advance options
                            </a>
                            {isModalOpen && (
                                <div className="my-2">
                                    <p className="text-md text-gray-500 pb-2">Knowledge base quality</p>
                                    <select
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        onChange={(e) => setQuality(e.target.value)}
                                    >
                                        <option value="single_page">Single Page</option>
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        <button
                            type="submit"
                            className="w-full py-2 px-4 bg-indigo-600 text-white font-bold rounded-lg shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
                        >
                            Submit
                        </button>
                        {message?.message && <p>Status: {message?.message}</p>}
                        {qr && (
                            <center>
                                <img src={qr} alt="QR Code" />
                            </center>
                        )}
                    </form>
                </div>
                <footer className="text-white mt-6 mb-8">© {new Date().getFullYear()} Gallabox. All Rights Reserved.</footer>
            </div>
        </NewLayout>
    );
};

export default WhatsappAIChatBot;
